// src/encounter/encounter-zoning.ts
//
// `?x=intel-always-open` · the encounter's zoning: both side columns (the left patient column and Scrips Intelligence) are
// open on arrival on every tab and every phase, and the work canvas never drops under its floor (Samer, 4 Nov: "the right and
// left rail stays open and active for the pre encounter and encounter and other tabs").
//
// The rules: Antonin's (scrips-signal-ds origin/feat/signal R-LAY-03, R-LAY-04, R-AI-01; catalog/src/shell/useFit.ts) as
// AMENDED by Samer on 4 Nov (claude-os docs/intelligence-rail-lock-2026-11-04.md §2: "wrong, patient summary is on the left,
// intelligence is on the right"):
//   · the left patient summary and the right Intelligence panel are two INDEPENDENT columns: opening or closing one never
//     folds or opens the other, and each toggle only acts on its own column;
//   · both stay docked at 1280; the centre takes what is left (480 at 1280 by this sum; the lock logs this against R-LAY-04's
//     640 for Antonin's ruling after the demo);
//   · only when the centre would drop under 480 (below about 1280 with both columns): the nav rail folds first, then
//     Intelligence becomes an overlay over the centre (closed, it is the orb button, which reopens the overlay). The left
//     summary stays docked;
//   · R-AI-01: docked, the panel is 400 and pushes the page.
// `fitZones` keeps useFit's canvas sum (rail, two insets, panel plus an inset, the left column) and its "nav rail first".
// The app's nav rail choice is treated as useFit's 'auto' (the rail always folds first for the floor).
//
// The widths are read from the page's CSS custom properties, as useFit reads its tokens (`tokenPx`): the DS this app ships
// (3.2.0) has tokens for the nav rail (`--zone-rail-collapsed` 56, `--zone-rail-expanded` 232) and the app one for the left
// column (`--enc-left-open` 320). It has NONE for the 400 panel (`--zone-panel` is 360), the floor (`--zone-canvas-min` is 680;
// the lock's amended floor is 480) or the 8 frame inset, and the bridge is frozen, so those three are in ZONE_FALLBACK
// (`--enc-intel-open` / `--enc-canvas-min` / `--enc-fit-inset` are read first, so a DS token can take over by that name).
//
// Choices (Close Intelligence, the left toggle) are kept in this module for the page session (until a reload), so they hold
// across tab switches and across the pre-visit / started-visit swap. Nothing is stored.
import { useLayoutEffect, useSyncExternalStore } from 'react';
import { railPreferredExpanded, setRailFitFolded, subscribeRailState } from '@/app/rail-state';
import { isEncounterFlagOn } from '@/shared/lib/encounter-flags';
import { setLeftPanelFolded, setLeftPanelZoningActive } from './left-panel-fold';

export type ZonePref = 'auto' | 'open' | 'closed';

export interface ZoneSizes {
  railOpen: number;
  railClosed: number;
  context: number;
  panel: number;
  inset: number;
  canvasMin: number;
}

/** Antonin's rulebook.css values (rail 232/56, context rail 320, panel 400, frame inset 8) and the lock's amended centre
 *  floor, 480 (§2, Samer 4 Nov; rulebook.css --canvas-min is 640, logged as a conflict for Antonin). */
export const ZONE_FALLBACK: ZoneSizes = { railOpen: 232, railClosed: 56, context: 320, panel: 400, inset: 8, canvasMin: 480 };

export interface FitInput {
  /** The viewport width (window.innerWidth). */
  vw: number;
  /** Whether the nav rail is open by the clinician's own state (before any fit fold). */
  railOpen: boolean;
  /** Whether the Intelligence panel is wanted (not closed by the clinician). */
  panelWanted: boolean;
  hasContext: boolean;
  contextPref: ZonePref;
  sizes?: ZoneSizes;
}

export interface FitDecision {
  /** The nav rail must fold for the floor (it was open). */
  railFolded: boolean;
  /** The left summary is docked open (only its own toggle closes it). */
  contextOpen: boolean;
  /** The Intelligence panel is docked open. */
  panelOpen: boolean;
  /** The floor keeps the Intelligence panel from docking: it is the overlay (or, closed, its orb button). */
  panelForced: boolean;
  /** The centre's width with this decision. */
  canvasWidth: number;
}

/** The zoning's decision: the nav rail folds first, then Intelligence leaves the row (overlay). The left summary is never folded
 *  here, and nothing here ties one side column to the other (lock §2, amended). */
export function fitZones({ vw, railOpen: railPrefOpen, panelWanted, hasContext, contextPref, sizes = ZONE_FALLBACK }: FitInput): FitDecision {
  const contextOpen = contextPref !== 'closed';
  const canvas = (railOpen: boolean, panelOn: boolean) =>
    vw -
    (railOpen ? sizes.railOpen : sizes.railClosed) -
    sizes.inset * 2 -
    (panelOn ? sizes.panel + sizes.inset : 0) -
    (hasContext && contextOpen ? sizes.context : 0);
  // The nav rail folds first (whatever the clinician chose for it: the floor wins).
  const railFor = (panelOn: boolean) => railPrefOpen && canvas(railPrefOpen, panelOn) >= sizes.canvasMin;
  let railOpen = railFor(panelWanted);
  const panelForced = panelWanted && canvas(railOpen, panelWanted) < sizes.canvasMin;
  if (panelForced) railOpen = railFor(false);
  const panelOpen = panelWanted && !panelForced;
  return { railFolded: railPrefOpen && !railOpen, contextOpen, panelOpen, panelForced, canvasWidth: canvas(railOpen, panelOpen) };
}

// ── The page session's choices ──────────────────────────────────────────────────────────────────────────────────────
let contextPref: ZonePref = 'auto';
let panelPref: ZonePref = 'auto';
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((l) => l());

/** The clinician opened or closed the left patient column. */
export function chooseContext(open: boolean): void {
  contextPref = open ? 'open' : 'closed';
  emit();
}

/** The clinician opened or closed Scrips Intelligence (Close Intelligence, the reopen disc, the DS chevron). */
export function choosePanel(open: boolean): void {
  panelPref = open ? 'open' : 'closed';
  emit();
}

/** Test seam: forget the choices. */
export function resetZoning(): void {
  contextPref = 'auto';
  panelPref = 'auto';
  emit();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  window.addEventListener('resize', listener);
  const offRail = subscribeRailState(listener);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('resize', listener);
    offRail();
  };
}

const snapshot = () => `${contextPref}|${panelPref}|${window.innerWidth}|${railPreferredExpanded() ? 1 : 0}`;

/** A CSS length custom property in px (useFit's `tokenPx`); the fallback when the page has no value for it. */
function tokenPx(name: string, fallback: number): number {
  const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  const n = parseFloat(raw);
  return Number.isFinite(n) && raw.endsWith('px') ? n : fallback;
}

function readSizes(): ZoneSizes {
  return {
    railOpen: tokenPx('--zone-rail-expanded', ZONE_FALLBACK.railOpen),
    railClosed: tokenPx('--zone-rail-collapsed', ZONE_FALLBACK.railClosed),
    context: tokenPx('--enc-left-open', ZONE_FALLBACK.context),
    panel: tokenPx('--enc-intel-open', ZONE_FALLBACK.panel),
    inset: tokenPx('--enc-fit-inset', ZONE_FALLBACK.inset),
    canvasMin: tokenPx('--enc-canvas-min', ZONE_FALLBACK.canvasMin),
  };
}

export interface EncounterZoning extends FitDecision {
  /** Scrips Intelligence shows over the centre (the pre-visit draws it so): reopened while the floor keeps it folded. */
  panelOverlay: boolean;
  /** The panel is on screen (docked or overlay). */
  panelShown: boolean;
  /** The left column is on screen (docked). */
  contextShown: boolean;
  /** The panel's width in px (Antonin's 400). */
  panelWidth: number;
  /** The clinician closed Intelligence (Close Intelligence): focus may move to its reopen button. */
  panelClosed: boolean;
}

/** Whether this browser has the zoning on (`?x=intel-always-open`). Read once per mount by the callers. */
export function isZoningOn(): boolean {
  return isEncounterFlagOn('intelAlwaysOpen');
}

/**
 * The zoning for the encounter on screen, or null when `enabled` is false (OFF: nothing here runs, and nothing is set on the
 * page). While mounted it writes the decision onto the page: the left column's fold (left-panel-fold.tsx), the nav rail's fit
 * fold (rail-state.ts), the root attributes `data-enc-zoning` / `data-enc-intel` (docked | overlay | folded), and the two width
 * custom properties the frozen bridge gives the right column (320 there): `--layout-intel-col` on the long page's
 * `encounter-shell` and `--enc-right-width` / `--enc-right-open` on the pre-visit's `pre-encounter-screen`. They are set on the
 * element (`style.setProperty`), the one way past the bridge's element rules without adding CSS (showcase-bridge.css is frozen:
 * scripts/check-no-hand-drawing.mjs H3), and removed when the page goes.
 */
export function useEncounterZoning(enabled: boolean): EncounterZoning | null {
  const key = useSyncExternalStore(enabled ? subscribe : noopSubscribe, enabled ? snapshot : emptySnapshot, emptySnapshot);
  const decision = enabled && key ? zoningFor() : null;
  const contextFolded = decision ? !decision.contextOpen : false;
  const railFolded = decision?.railFolded ?? false;
  const intel = decision ? (decision.panelOpen ? 'docked' : decision.panelOverlay ? 'overlay' : 'folded') : null;
  const panelPx = decision ? `${decision.panelWidth}px` : '';
  useLayoutEffect(() => {
    if (!enabled) return;
    setLeftPanelFolded(contextFolded);
  }, [enabled, contextFolded]);
  useLayoutEffect(() => {
    if (!enabled) return;
    setRailFitFolded(railFolded);
  }, [enabled, railFolded]);
  useLayoutEffect(() => {
    if (!enabled) return;
    // While mounted, left-panel-fold's own 1280 resize rule stands aside (the zoning decides the fold).
    setLeftPanelZoningActive(true);
    return () => {
      setLeftPanelZoningActive(false);
      setRailFitFolded(false);
    };
  }, [enabled]);
  useLayoutEffect(() => {
    if (!enabled || !intel) return;
    const root = document.documentElement;
    root.dataset.encZoning = 'on';
    root.dataset.encIntel = intel;
    return () => {
      delete root.dataset.encZoning;
      delete root.dataset.encIntel;
    };
  }, [enabled, intel]);
  // Every render: the shell's elements can be new after a tab switch or the pre-visit / started swap.
  useLayoutEffect(() => {
    if (!enabled || !panelPx) return;
    const set: Array<[HTMLElement, string, string]> = [];
    document.querySelectorAll<HTMLElement>('[data-slot="encounter-shell"]').forEach((el) => {
      set.push([el, '--layout-intel-col', panelPx]);
      // The long page's grid floors the centre at the DS's 560 (`minmax(--layout-note-col-min, 1fr)`) and lets the left column
      // shrink (`minmax(0, --layout-chart-col)`), so at 1280 and below an open left column was cut (measured on dev at 800:
      // track 173, content 320). With the zoning the centre gives up the width instead: the left column's track is its full
      // 320 whenever it is open, and the zoning's own floor (480, docked Intelligence) decides the rest.
      set.push([el, '--layout-note-col-min', '0px']);
      // Closed, the left column takes no track: showcase/encounter-shell.css pins `--layout-chart-col` to the open width on
      // the long page, so the hidden column kept reserving 320 and squeezed the centre (Codex, third pass).
      if (contextFolded) set.push([el, '--layout-chart-col', '0px']);
    });
    document.querySelectorAll<HTMLElement>('[data-slot="pre-encounter-screen"]').forEach((el) => {
      // Docked, the row leaves the rail its 400; closed or overlay it leaves nothing, so no empty gutter (Codex, seventh pass).
      set.push([el, '--enc-right-width', decision?.panelOpen ? panelPx : '0px'], [el, '--enc-right-open', panelPx]);
    });
    set.forEach(([el, name, value]) => el.style.setProperty(name, value));
    return () => set.forEach(([el, name]) => el.style.removeProperty(name));
  });
  return decision;
}

function zoningFor(): EncounterZoning {
  const sizes = readSizes();
  const base = { vw: window.innerWidth, railOpen: railPreferredExpanded(), hasContext: true, contextPref, sizes };
  const d = fitZones({ ...base, panelWanted: panelPref !== 'closed' });
  // Under the floor, Intelligence is the overlay whenever it is wanted (open on arrival, or reopened from its orb button).
  const panelOverlay = d.panelForced;
  return { ...d, panelOverlay, panelShown: d.panelOpen || panelOverlay, contextShown: d.contextOpen, panelWidth: sizes.panel, panelClosed: panelPref === 'closed' };
}

const noopSubscribe = () => () => {};
const emptySnapshot = () => '';
